import React from 'react'
import PropTypes from 'prop-types'
import { compose, setDisplayName, withHandlers, withState } from 'recompose'
import { CellMeasurer, CellMeasurerCache, List as ListVirtualized, AutoSizer } from 'react-virtualized'
import classNames from 'classnames'

import { DEFAULT_LIST_SIZE } from 'store/common/constants/generic'
import { withComponentDidUpdate } from 'hoc/withComponentDidUpdate'
import { rowContainerRendererHandler } from 'components/list/helpers/listHelpers'

import './List.scss'

const DEFAULT_LIST_HEIGHT = 300

const ListCmp = ({
  autoHeight = false,
  className,
  height = DEFAULT_LIST_HEIGHT,
  rowWidth = 500,
  list,
  handleSetListRef,
  rowContainerRendererHandler,
  rowCount = list.length,
  onRowsRendered,
  overscanRowCount = DEFAULT_LIST_SIZE,
  // `CellMeasurer` will calculate height if default is not provided
  rowHeight,
  noRowsRenderer = () => null,

  // autoSizer props
  disableHeight = true,
}) => {
  const cache = new CellMeasurerCache({
    defaultHeight: rowHeight,
    minHeight: rowHeight,
    fixedWidth: true,
  })

  return (
    <AutoSizer disableHeight={disableHeight}>
      {({ width }) => (
        <ListVirtualized
          autoHeight={autoHeight}
          className={classNames('ui-virtualized-list', className)}
          height={height}
          list={list}
          noRowsRenderer={noRowsRenderer}
          onRowsRendered={onRowsRendered}
          overscanRowCount={overscanRowCount}
          ref={handleSetListRef}
          rowCount={rowCount}
          rowHeight={cache.rowHeight}
          deferredMeasurementCache={cache}
          rowRenderer={props => (
            // eslint-disable-next-line
            <CellMeasurer key={props.key} cache={cache} columnIndex={0} rowIndex={props.index} parent={props.parent}>
              {rowContainerRendererHandler(props)}
            </CellMeasurer>
          )}
          // 500 - need only for tests
          // in test width always equal 0
          width={width || rowWidth}
        />
      )}
    </AutoSizer>
  )
}

ListCmp.propTypes = {
  autoHeight: PropTypes.bool,
  className: PropTypes.string,
  height: PropTypes.number,
  list: PropTypes.array.isRequired,
  handleSetListRef: PropTypes.func.isRequired,
  rowContainerRendererHandler: PropTypes.func.isRequired,
  rowCount: PropTypes.number,
  rowHeight: PropTypes.number,
  onRowsRendered: PropTypes.func,
  noRowsRenderer: PropTypes.func,
  overscanRowCount: PropTypes.number,
  rowWidth: PropTypes.number,

  // autoSizer props
  disableHeight: PropTypes.bool,
}

const handleSetListRef =
  ({ registerChild, setListRef }) =>
  ref => {
    registerChild && registerChild(ref)
    setListRef(ref)
  }

const handleComponentDidUpdate = (nextProps, prevProps) => {
  const isRefUpdated = nextProps.scrollToIndex && nextProps.listRef !== prevProps.listRef
  const isScrollToIndexUpdated = nextProps.listRef && nextProps.scrollToIndex !== prevProps.scrollToIndex

  if (isRefUpdated || isScrollToIndexUpdated) {
    // NOTE: This is a workaround for bugs of 'react-virtualized'
    // See: https://github.com/bvaughn/react-virtualized/issues/803
    // See: https://github.com/bvaughn/react-virtualized/issues/995

    // Initial scroll
    nextProps.listRef.scrollToRow(nextProps.scrollToIndex)
    // Second scroll as a workaround
    setTimeout(() => nextProps.listRef.scrollToRow(nextProps.scrollToIndex))
  }
}

export const List = compose(
  setDisplayName('List'),
  withState('listRef', 'setListRef', null),
  withHandlers({
    handleSetListRef,
    rowContainerRendererHandler,
  }),
  withComponentDidUpdate(handleComponentDidUpdate),
)(ListCmp)
